import "whatwg-fetch";
import firebase from "firebase/app";
import "firebase/storage";
import { Role, User } from "../model/user";
import { fireBaseConfig } from "../../config/config";
import { AppError, ErrorType } from "../model/app_error";
import {
  TDownloadFileObject,
  TExternalContractActivityFiles,
  TFileObject,
  TFileUrlWithActivityId,
} from "../../presentation/type/common";
import { END } from "redux-saga";
import { getFileDownloadURL } from "../../infra/api/service/resource_api";

const baseURL = `gs://${fireBaseConfig.storageBucket}`;

interface IFileUseCase {
  createCompanyLogo(user: User, uploadFile: File): Promise<string>;

  createProjectThumbnail(
    user: User,
    uploadFile: File,
    projectId: string
  ): Promise<string>;

  createClientIcon(user: User, uploadFile: File): Promise<string>;

  createCustomerIcon(user: User, uploadFile: File): Promise<string>;

  createMessageFile(
    user: User,
    uploadFile: File,
    projectId: string,
    opponentId: string
  ): Promise<string>;

  createContractFile(
    user: User,
    uploadFile: File,
    companyId: string,
    projectId: string,
    customerId: string
  ): Promise<string>;

  createContractFileForCustomer(
    user: User,
    uploadFile: File,
    companyId: string,
    projectId: string
  ): Promise<string>;

  createExternalContractActivityFile(
    user: User,
    uploadFile: File,
    externalContractActivityId: string
  ): Promise<TFileObject>;

  observeExternalContractActivityFile(
    user: User,
    uploadFile: File,
    callback: (
      input: { success?: TFileObject; progress?: number; error?: any } | END
    ) => void
  ): any;

  deleteExternalContractActivityFile(fileObject: TFileObject): Promise<void>;

  getExternalContractActivityFiles(
    fileUrlWithActivityId: TFileUrlWithActivityId
  ): Promise<TExternalContractActivityFiles>;

  getExternalContractActivityFile(
    user: User,
    fileName: string
  ): Promise<TDownloadFileObject>;

  convertGStoHTTPS(gsURL: string): Promise<string>;
}

class FileUseCase implements IFileUseCase {
  public createCompanyLogo = (
    user: User,
    uploadFile: File
  ): Promise<string> => {
    let companyId: string;
    let clientId: string;
    let fileName: string;

    return new Promise<string>((resolve, reject) => {
      switch (user.role) {
        case Role.Role_Client:
          if (!user.client) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          companyId = user.client.companyID;
          clientId = user.client.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;

      const refURL: string = `${baseURL}/company/${companyId}/${clientId}/${fileName}`;

      return firebase
        .storage()
        .refFromURL(refURL)
        .put(uploadFile)
        .then((snapshot: firebase.storage.UploadTaskSnapshot) => {
          resolve(refURL);
        })
        .catch((error) => {
          reject(this.firbaseStorageErrorHandle(error));
        });
    });
  };

  public createProjectThumbnail = (
    user: User,
    uploadFile: File,
    projectId: string
  ): Promise<string> => {
    let clientId: string;
    let fileName: string;

    return new Promise<string>((resolve, reject) => {
      switch (user.role) {
        case Role.Role_Client:
          if (!user.client) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          clientId = user.client.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;

      const refURL: string = `${baseURL}/project/${projectId}/${clientId}/${fileName}`;

      return firebase
        .storage()
        .refFromURL(refURL)
        .put(uploadFile)
        .then((snapshot: firebase.storage.UploadTaskSnapshot) => {
          resolve(refURL);
        })
        .catch((error) => {
          reject(this.firbaseStorageErrorHandle(error));
        });
    });
  };

  public createClientIcon = (user: User, uploadFile: File): Promise<string> => {
    let clientId: string;
    let fileName: string;

    return new Promise<string>((resolve, reject) => {
      switch (user.role) {
        case Role.Role_Client:
          if (!user.client) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          clientId = user.client.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;

      const refURL: string = `${baseURL}/client/${clientId}/${fileName}`;

      return firebase
        .storage()
        .refFromURL(refURL)
        .put(uploadFile)
        .then((snapshot: firebase.storage.UploadTaskSnapshot) => {
          resolve(refURL);
        })
        .catch((error) => {
          reject(this.firbaseStorageErrorHandle(error));
        });
    });
  };

  public createCustomerIcon = (
    user: User,
    uploadFile: File
  ): Promise<string> => {
    let customerId: string;
    let fileName: string;

    return new Promise<string>((resolve, reject) => {
      switch (user.role) {
        case Role.Role_Customer:
          if (!user.customer) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          customerId = user.customer.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;

      const refURL: string = `${baseURL}/customer/${customerId}/${fileName}`;

      return firebase
        .storage()
        .refFromURL(refURL)
        .put(uploadFile)
        .then((snapshot: firebase.storage.UploadTaskSnapshot) => {
          resolve(refURL);
        })
        .catch((error) => {
          reject(this.firbaseStorageErrorHandle(error));
        });
    });
  };

  public createMessageFile = (
    user: User,
    uploadFile: File,
    projectId: string,
    opponentId: string
  ): Promise<string> => {
    let ownId: string;
    let fileName: string;

    return new Promise<string>((resolve, reject) => {
      switch (user.role) {
        case Role.Role_Customer:
          if (!user.customer) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          ownId = user.customer.id;
          break;
        case Role.Role_Client:
          if (!user.client) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          ownId = user.client.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;

      const refURL: string = `${baseURL}/message/${projectId}/${ownId}/${opponentId}/${fileName}`;

      return firebase
        .storage()
        .refFromURL(refURL)
        .put(uploadFile)
        .then((snapshot) => {
          resolve(refURL);
        })
        .catch((error) => {
          reject(this.firbaseStorageErrorHandle(error));
        });
    });
  };

  public createContractFile = (
    user: User,
    uploadFile: File,
    companyId: string,
    projectId: string,
    customerId: string
  ): Promise<string> => {
    let clientId: string;
    let fileName: string;

    return new Promise<string>((resolve, reject) => {
      switch (user.role) {
        case Role.Role_Client:
          if (!user.client) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          clientId = user.client.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;

      const refURL: string = `${baseURL}/contract/${companyId}/${clientId}/${projectId}/${customerId}/${fileName}`;

      return firebase
        .storage()
        .refFromURL(refURL)
        .put(uploadFile)
        .then((snapshot: firebase.storage.UploadTaskSnapshot) => {
          resolve(refURL);
        })
        .catch((error) => {
          reject(this.firbaseStorageErrorHandle(error));
        });
    });
  };

  public createContractFileForCustomer = (
    user: User,
    uploadFile: File,
    companyId: string,
    projectId: string
  ): Promise<string> => {
    let customerId: string;
    let fileName: string;

    return new Promise<string>((resolve, reject) => {
      switch (user.role) {
        case Role.Role_Customer:
          if (!user.customer) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          customerId = user.customer.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;

      const refURL: string = `${baseURL}/contract/${companyId}/${projectId}/${customerId}/${fileName}`;

      return firebase
        .storage()
        .refFromURL(refURL)
        .put(uploadFile)
        .then((snapshot: firebase.storage.UploadTaskSnapshot) => {
          resolve(refURL);
        })
        .catch((error) => {
          reject(this.firbaseStorageErrorHandle(error));
        });
    });
  };

  public createExternalContractActivityFile = (
    user: User,
    uploadFile: File,
    externalContractActivityId: string
  ): Promise<TFileObject> => {
    let customerId: string;
    let fileName: string;

    return new Promise<TFileObject>((resolve, reject) => {
      switch (user.role) {
        case Role.Role_Customer:
          if (!user.customer) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          customerId = user.customer.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;
      const originalName = uploadFile.name;
      const fileSize = uploadFile.size;
      const metaData = {
        contentDisposition: `attachment; filename=${uploadFile.name}`,
        customMetadata: {
          originalFileName: uploadFile.name,
          activityId: externalContractActivityId,
        },
      };

      const refURL: string = `${baseURL}/external_contract_activity/${customerId}/${fileName}`;

      return firebase
        .storage()
        .refFromURL(refURL)
        .put(uploadFile, metaData)
        .then((snapshot: firebase.storage.UploadTaskSnapshot) => {
          const fileObject: TFileObject = {
            fileURL: refURL,
            originalName,
            fileName,
            fileSize,
          };
          resolve(fileObject);
        })
        .catch((error) => {
          reject(this.firbaseStorageErrorHandle(error));
        });
    });
  };

  public observeExternalContractActivityFile = (
    user: User,
    uploadFile: File,
    callback: (
      input: { success?: TFileObject; progress?: number; error?: any } | END
    ) => void
  ): any => {
    let customerId: string = "";
    let fileName: string;

    switch (user.role) {
      case Role.Role_Customer:
        if (!user.customer) {
          callback({
            error: new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "User情報がありませせん"
            ),
          });
          break;
        }
        customerId = user.customer.id;
        break;
      default:
        callback({
          error: new AppError(
            "UNKNOWN CODE",
            ErrorType.CLIENT_ERROR,
            "異常が発生しました"
          ),
        });
    }

    fileName = `${this.getUniqueStr()}.${this.getExtention(uploadFile.name)}`;
    const originalName = uploadFile.name;
    const fileSize = uploadFile.size;
    const metaData = {
      contentDisposition: `inline; filename=${originalName}`,
      customMetadata: {
        originalFileName: originalName,
      },
    };

    const refURL: string = `${baseURL}/external_contract_activity/${customerId}/${fileName}`;
    const uploadTask = firebase
      .storage()
      .refFromURL(refURL)
      .put(uploadFile, metaData);
    const unsubscribe = uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        callback({ progress });
        switch (snapshot.state) {
          case firebase.storage.TaskState.PAUSED: // or 'paused'
            break;
          case firebase.storage.TaskState.RUNNING: // or 'running'
            break;
        }
      },
      (error) => {
        callback({ error });
        callback(END);
        unsubscribe(); // error時ににunsubscribe
      },
      () => {
        const fileObject: TFileObject = {
          fileURL: refURL,
          originalName,
          fileName,
          fileSize,
        };

        callback({ success: fileObject });
        callback(END);
        unsubscribe(); // 成功時にunsubscribe
      }
    );

    return unsubscribe;
  };

  public getExternalContractActivityFiles = async (
    fileUrlWithActivityId: TFileUrlWithActivityId
  ): Promise<TExternalContractActivityFiles> => {
    return new Promise<TExternalContractActivityFiles>(
      async (resolve, reject) => {
        const itemList = await Promise.all(
          fileUrlWithActivityId.fileURLs.map(async (fileURL) => {
            const itemRef = firebase.storage().refFromURL(fileURL);
            const metaData = await itemRef
              .getMetadata()
              .catch((error) => reject(error));
            const customMetaData = metaData
              ? metaData.customMetadata
              : undefined;
            const originalName = customMetaData
              ? customMetaData.originalFileName
              : "";
            const fileName = itemRef.name;
            const fileSize = metaData ? metaData.size : 0;
            const fileObject: TFileObject = {
              fileURL,
              originalName,
              fileName,
              fileSize,
            };
            return fileObject;
          })
        );
        const activityId = fileUrlWithActivityId.activityId;
        const res: TExternalContractActivityFiles = {
          id: activityId,
          files: itemList,
        };

        resolve(res);
      }
    );
  };

  public getExternalContractActivityFile = async (
    user: User,
    fileName: string
  ): Promise<TDownloadFileObject> => {
    let customerId: string;
    return new Promise<TDownloadFileObject>(async (resolve, reject) => {
      switch (user.role) {
        case Role.Role_Customer:
          if (!user.customer) {
            reject(
              new AppError(
                "UNKNOWN CODE",
                ErrorType.CLIENT_ERROR,
                "User情報がありませせん"
              )
            );
            break;
          }
          customerId = user.customer.id;
          break;
        default:
          reject(
            new AppError(
              "UNKNOWN CODE",
              ErrorType.CLIENT_ERROR,
              "異常が発生しました"
            )
          );
      }

      const refURL: string = `${baseURL}/external_contract_activity/${customerId}/${fileName}`;

      const downloadURL = await firebase
        .storage()
        .refFromURL(refURL)
        .getDownloadURL()
        .catch((error) => reject(error));

      const metaData = await firebase
        .storage()
        .refFromURL(refURL)
        .getMetadata()
        .catch((error) => reject(error));
      const customMetaData = metaData ? metaData.customMetadata : undefined;
      const originalName = customMetaData
        ? customMetaData.originalFileName
        : "";
      const type = metaData ? metaData.contentType : "";

      const downloadFileObject: TDownloadFileObject = {
        downloadURL,
        originalName,
        fileName,
        type,
      };
      resolve(downloadFileObject);
    });
  };

  public deleteExternalContractActivityFile(
    fileObject: TFileObject
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const refURL: string = fileObject.fileURL;
      return firebase
        .storage()
        .refFromURL(refURL)
        .delete()
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public convertGStoHTTPS = (gsURL: string): Promise<string> => {
    // Private attachments are authorized by the API before a download URL is issued.
    if (/^gs:\/\/[^/]+\/(message|contract)\//.test(gsURL)) {
      return getFileDownloadURL(gsURL);
    }
    return new Promise((resolve, reject) => {
      // 日本語ファイル対応のため、decode実施
      const refURL: string = `${decodeURI(gsURL)}`;

      firebase
        .storage()
        .refFromURL(refURL)
        .getDownloadURL()
        .then((downloadURL) => {
          resolve(downloadURL);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  private getUniqueStr = (myStrong?: number): string => {
    let strong = 1000;
    if (myStrong) {
      strong = myStrong;
    }
    return (
      new Date().getTime().toString(16) +
      Math.floor(strong * Math.random()).toString(16)
    );
  };

  private getExtention = (filename: string): string => {
    const f = filename.split(".");
    return f[f.length - 1].toLowerCase();
  };

  private firbaseStorageErrorHandle = (error: any): string => {
    return `${error.code}:${error.message}`;
  };
}

const createFileUsecase = (): IFileUseCase => {
  return new FileUseCase();
};

export { IFileUseCase, createFileUsecase };



// WEBPACK FOOTER //
// ./src/domain/usecase/file_usecase.ts